import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { socialLinksStore, SocialLink } from "@/store/socialLinksStore";
import { siteSettingsStore, SiteSettings } from "@/store/siteSettingsStore";

const REMOTE_DEFAULT_LOGO =
  "https://static.readdy.ai/image/433f60a06dda16b0e3fd209711ce4024/45c8d4d7d672c9e272b7c78f92025684.png";
const ENGLISH_LOGO = "/assets/en-logo.png";
const LOCAL_ARABIC_LOGO = `${__BASE_PATH__.replace(/\/?$/, "/")}assets/ar-logo.png`;

export default function Footer() {
  const { t, i18n } = useTranslation();
  const isEnglish = i18n.language === "en";
  const isRtl = !isEnglish;
  const [socialLinks, setSocialLinks] = useState<SocialLink[]>([]);
  const [siteSettings, setSiteSettings] = useState<SiteSettings | null>(null);

  useEffect(() => {
    socialLinksStore.getActive().then(setSocialLinks);
    siteSettingsStore.get().then(setSiteSettings);
  }, []);

  const configuredLogo = siteSettings?.logoUrl;
  const logoSrc = isEnglish
    ? ENGLISH_LOGO
    : !configuredLogo || configuredLogo === REMOTE_DEFAULT_LOGO
      ? LOCAL_ARABIC_LOGO
      : configuredLogo;
  const siteName = isEnglish ? t("brand.name") : siteSettings?.siteName || t("brand.name");
  const logoAlt = isEnglish ? "Alsiyahiah" : siteName;

  const newsLinks = [
    { key: "footer.news.aviation", path: "/category/aviation" },
    { key: "footer.news.hotels", path: "/category/hotels" },
    { key: "footer.news.destinations", path: "/category/destinations" },
    { key: "footer.news.culture", path: "/category/culture" },
    { key: "footer.news.family", path: "/category/family" },
    { key: "footer.news.tech", path: "/category/tech" },
    { key: "footer.news.reports", path: "/category/reports" },
  ];

  const specialLinks = [
    { key: "footer.special.reports", path: "/category/reports" },
    { key: "footer.special.gcc", path: "/gcc-guide" },
    { key: "footer.special.events", path: "/category/events" },
    { key: "footer.special.sports", path: "/category/sports" },
    { key: "footer.special.infographics", path: "/infographics" },
    { key: "footer.special.about", path: "/about" },
    { key: "footer.special.advertise", path: "/advertising" },
  ];

  const legalLinks = [
    "footer.privacy",
    "footer.terms",
    "footer.policy",
    "footer.ads",
  ];

  return (
    <footer className="bg-[#0F172A] text-white" dir={isRtl ? "rtl" : "ltr"}>
      <div className="h-1 bg-gradient-to-r from-[#D4AF37] via-[#f0d060] to-[#D4AF37]" />

      <div className="max-w-screen-xl mx-auto px-6 md:px-10 pt-14 pb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mb-10">
          {/* Brand */}
          <div className="lg:col-span-1">
            <img
              src={logoSrc}
              alt={logoAlt}
              className={`h-10 w-auto object-contain mb-4 ${isEnglish ? "" : "brightness-0 invert"}`}
            />
            <p className="text-gray-400 text-sm leading-relaxed mb-5">
              {t("footer.about")}
            </p>
            <div className="mb-5">
              <p className="text-gray-500 text-xs mb-3 uppercase tracking-wider">{t("footer.followUs")}</p>
              <div className="flex items-center gap-2 flex-wrap">
                {/* Instagram — always visible */}
                <a
                  href="https://www.instagram.com/alsiyahiah"
                  target="_blank"
                  rel="nofollow noreferrer"
                  title={t("social.instagram")}
                  className="w-9 h-9 flex items-center justify-center rounded-full bg-gradient-to-r from-[#833AB4] via-[#E1306C] to-[#F77737] text-white hover:opacity-85 transition-all duration-200 cursor-pointer"
                >
                  <i className="ri-instagram-line text-sm" />
                </a>
                {socialLinks
                  .filter((link) => link.url && link.platform !== "instagram")
                  .map((link) => (
                    <a
                      key={link.id}
                      href={link.url}
                      target="_blank"
                      rel="nofollow noreferrer"
                      title={link.label}
                      className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-[#D4AF37] text-gray-300 hover:text-white transition-all duration-200 cursor-pointer"
                    >
                      <i className={`${link.icon} text-sm`} />
                    </a>
                  ))}
              </div>
            </div>
            <div className="flex items-center gap-2 text-gray-500 text-xs">
              <i className="ri-map-pin-line text-[#D4AF37]" />
              {t("footer.location")}
            </div>
          </div>

          {/* News Sections */}
          <div>
            <h4 className="font-semibold text-sm mb-5 text-[#D4AF37] uppercase tracking-wider">
              {t("footer.newsTitle")}
            </h4>
            <ul className="space-y-2.5">
              {newsLinks.map(({ key, path }) => (
                <li key={key}>
                  <Link
                    to={path}
                    className="text-gray-400 text-sm hover:text-[#D4AF37] transition-colors cursor-pointer flex items-center gap-2 group"
                  >
                    <span className="w-1 h-1 rounded-full bg-[#D4AF37]/30 group-hover:bg-[#D4AF37] transition-colors shrink-0" />
                    {t(key)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Special Sections */}
          <div>
            <h4 className="font-semibold text-sm mb-5 text-[#D4AF37] uppercase tracking-wider">
              {t("footer.specialTitle")}
            </h4>
            <ul className="space-y-2.5">
              {specialLinks.map(({ key, path }) => (
                <li key={key}>
                  <Link
                    to={path}
                    className="text-gray-400 text-sm hover:text-[#D4AF37] transition-colors cursor-pointer flex items-center gap-2 group"
                  >
                    <span className="w-1 h-1 rounded-full bg-[#D4AF37]/30 group-hover:bg-[#D4AF37] transition-colors shrink-0" />
                    {t(key)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-semibold text-sm mb-5 text-[#D4AF37] uppercase tracking-wider">
              {t("footer.contactTitle")}
            </h4>
            <ul className="space-y-3 mb-6">
              <li>
                <a
                  href="tel:0097339117226"
                  className="flex items-center gap-3 text-sm text-gray-400 hover:text-[#D4AF37] transition-colors cursor-pointer group"
                >
                  <span className="w-7 h-7 flex items-center justify-center rounded-full bg-[#D4AF37]/15 group-hover:bg-[#D4AF37]/25 transition-colors shrink-0">
                    <i className="ri-phone-line text-[#D4AF37] text-xs" />
                  </span>
                  0097339117226
                </a>
              </li>
              <li>
                <a
                  href="https://wa.me/0097336370441"
                  target="_blank"
                  rel="nofollow noreferrer"
                  className="flex items-center gap-3 text-sm text-gray-400 hover:text-[#25D366] transition-colors cursor-pointer group"
                >
                  <span className="w-7 h-7 flex items-center justify-center rounded-full bg-[#25D366]/15 group-hover:bg-[#25D366]/25 transition-colors shrink-0">
                    <i className="ri-whatsapp-line text-[#25D366] text-xs" />
                  </span>
                  {t("footer.whatsapp")}
                </a>
              </li>
              <li>
                <a
                  href="mailto:info@alsiyahiah.com"
                  className="flex items-center gap-3 text-sm text-gray-400 hover:text-[#D4AF37] transition-colors cursor-pointer group"
                >
                  <span className="w-7 h-7 flex items-center justify-center rounded-full bg-[#D4AF37]/15 group-hover:bg-[#D4AF37]/25 transition-colors shrink-0">
                    <i className="ri-mail-line text-[#D4AF37] text-xs" />
                  </span>
                  info@alsiyahiah.com
                </a>
              </li>
              <li>
                <a
                  href="mailto:ads@alsiyahiah.com"
                  className="flex items-center gap-3 text-sm text-gray-400 hover:text-[#D4AF37] transition-colors cursor-pointer group"
                >
                  <span className="w-7 h-7 flex items-center justify-center rounded-full bg-[#D4AF37]/15 group-hover:bg-[#D4AF37]/25 transition-colors shrink-0">
                    <i className="ri-mail-send-line text-[#D4AF37] text-xs" />
                  </span>
                  ads@alsiyahiah.com
                </a>
              </li>
            </ul>
            <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/20 rounded-xl p-4">
              <p className="text-[#D4AF37] text-xs font-semibold mb-2">
                {t("footer.newsEmailLabel")}
              </p>
              <a
                href={`mailto:${t("footer.newsEmail")}`}
                className="text-gray-300 text-xs hover:text-[#D4AF37] transition-colors cursor-pointer"
              >
                {t("footer.newsEmail")}
              </a>
              <p className="text-gray-500 text-xs mt-2">{t("footer.workHours")}</p>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-white/8 pt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-gray-500 text-sm">{t("footer.copyright")}</p>
          <div className="flex items-center gap-4">
            {legalLinks.map((key) => (
              <a
                key={key}
                href="#"
                className="text-gray-500 text-xs hover:text-[#D4AF37] transition-colors cursor-pointer"
              >
                {t(key)}
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}
